/**
 * 업무 위키 API 서비스
 *
 * ⛔ 경로에서 앞의 `/api` 를 빼고 쓴다. apiClient 의 baseURL 이 이미 `/api` 라
 *    `/api/wiki/...` 로 쓰면 `/api/api/wiki/...` 가 되어 404 다 (erp-context.md §5).
 */

import api from './api/apiClient';

export interface WikiEntityType {
  entity_type: string;
  display_name: string;
  audit_entity_type: string | null;
  change_table: string | null;
  deep_link: string | null;
  sort_order: number;
}

export interface WikiBlockSource {
  id: string;
  source_kind: 'table' | 'audit' | 'meeting' | 'approval' | 'file' | 'url';
  table_name: string | null;
  record_id: string | null;
  column_name: string | null;
  url: string | null;
  observed_at: string;
  note: string;
}

export interface WikiBlock {
  id: string;
  ord: number;
  block_type: 'text' | 'rule' | 'decision' | 'exception' | 'callout';
  content_md: string;
  content_plain: string;
  /** 'human' 사람이 씀 / 'ai' AI 초안 */
  origin: 'human' | 'ai';
  /** 'asserted' 원천에 그대로 있음 / 'inferred' 여러 원천을 합쳐 추론함 */
  claim_basis: 'asserted' | 'inferred';
  status: 'draft' | 'published' | 'archived';
  /** 공개 방식 — 'human' 사람 검토 공개 / 'bulk-auto' 대기분 일괄 자동공개 / 'ai-auto' 수집 자동공개 */
  publish_source?: 'human' | 'bulk-auto' | 'ai-auto';
  evidence_checked_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  /** 쓴 사람(users.id). 메모는 쓴 사람/매니저+만 고친다 (PR-3) */
  created_by?: string | null;
  /** 수집 원문이면 값이 있다 — 있으면 «누구도 고칠 수 없다»(정리본을 고친다) */
  ingest_source_ref?: string | null;
  /** 마지막 수정 시각. 공개할 때 «내가 읽은 그 시점» 으로 돌려보낸다 */
  updated_at: string;
  sources: WikiBlockSource[];
  /** 근거 확인이 오래됐거나 한 번도 확인하지 않음 */
  needs_review: boolean;
}

export interface WikiTimelineItem {
  source: 'audit' | 'change';
  occurred_at: string;
  actor_name: string | null;
  event: string;
  detail: Record<string, unknown> | null;
}

export interface WikiEntityPage {
  entity: { type: string; typeLabel: string; id: string; deepLink: string | null };
  page: { id: string; title: string; summary: string; status: string } | null;
  /** ① 지금 — 원천 행 그대로. 없을 수 있다(삭제된 항목) */
  current: Record<string, unknown> | null;
  /** ② 규칙 */
  blocks: WikiBlock[];
  /** ③ 이력 */
  timeline: WikiTimelineItem[];
  timelineCursor: string | null;
  links: { self: string };
}

export interface WikiSearchBlock
  extends Pick<WikiBlock, 'id' | 'content_plain' | 'status' | 'origin' | 'block_type'> {
  page_id: string;
  updated_at: string;
  page: { id: string; kind: string; entity_type: string | null; entity_id: string | null; title: string } | null;
  /** 화면이 그대로 쓸 이동 주소. 만들 수 없으면 null — 이동을 막아야 한다 */
  path: string | null;
}

/** 통합 검색 결과 — 문서 단위로 묶은 것(문단·원본 지식문서 중복 제거) */
export interface WikiSearchGroup {
  key: string;
  title: string;
  path: string | null;
  kind: string;            // 'doc' | 'entity'
  published: number;       // 이 문서에서 매치된 승인 문단 수
  draft: number;           // 검토 전 문단 수
  snippets: string[];      // 매치된 문단 원문(최대 2) — 화면에서 줄 수를 제한한다
}

export interface WikiSearchResult {
  blocks: WikiSearchBlock[];
  documents: Array<{ id: string; title: string; summary: string; status: string; updated_at: string }>;
  /** 홈 화면이 쓰는 통합 목록. 서버가 문서 단위로 합쳐 준다 */
  groups?: WikiSearchGroup[];
}

// ── 라이브 토큰 (S9-3) ────────────────────────────────
// 문서에 「재고 12개」라고 적으면 다음 날 틀린다. `{{재고:K-20}}` 이라고 적어 두면
// 볼 때마다 실제 값을 채운다.

export interface WikiMetric {
  metric_key: string;
  display_name: string;
  lookup_label: string;
  unit: string;
  description: string;
}

export interface ResolvedMetric {
  key: string;
  lookup: string;
  isFound: boolean;
  value: string | null;
  unit: string | null;
  /** 값 + 단위. 못 찾았으면 null — 화면은 토큰을 글자 그대로 남긴다 */
  display: string | null;
}

/**
 * 토큰 문법 — `{{지표:조회값}}`
 *
 * ⛔ 서버(`src/services/wiki/wiki-metric-service.js`)에 같은 정규식이 있다.
 *    한쪽만 고치면 화면은 토큰으로 보는데 서버는 아니라고 하는 상태가 된다.
 */
export const WIKI_TOKEN_RE = /\{\{\s*([^:{}\s][^:{}]{0,19})\s*:\s*([^{}]{1,64}?)\s*\}\}/g;

/** 채워 넣은 값을 찾을 때 쓰는 열쇠. 조회값에 공백이 들어갈 수 있어 NUL 로 잇는다. */
export function metricCacheKey(key: string, lookup: string): string {
  return `${key}\u0000${lookup}`;
}

/** 글에서 토큰을 찾아낸다. 같은 토큰이 여러 번 나와도 한 번만 돌려준다. */
export function parseWikiTokens(text: string): Array<{ key: string; lookup: string }> {
  if (!text) return [];
  const seen = new Set<string>();
  const out: Array<{ key: string; lookup: string }> = [];
  WIKI_TOKEN_RE.lastIndex = 0;
  let m = WIKI_TOKEN_RE.exec(text);
  while (m) {
    const key = m[1].trim();
    const lookup = m[2].trim();
    const id = metricCacheKey(key, lookup);
    if (key && lookup && !seen.has(id)) {
      seen.add(id);
      out.push({ key, lookup });
    }
    m = WIKI_TOKEN_RE.exec(text);
  }
  return out;
}

// ── 근거 고르기 (S13) ─────────────────────────────────
// 표 이름을 손으로 적지 않게 한다. 화면은 «항목 종류» 만 알고, 어느 표인지는 서버가 정한다.

// ── 관계 지도 (신경망 M1) ─────────────────────────────

export interface WikiGraphNode {
  id: string;
  slug: string;
  title: string;
  summary: string;
  /** 문서 분류 이름 — 지도 범례에 쓴다 */
  category: string;
  /** 분류 slug ('__uncategorized' = 미분류) — PR-3 */
  categorySlug: string;
  /** 분류 색 인덱스(시드 고정). null = 미분류 */
  paletteIndex: number | null;
  publishedCount: number;
  draftCount: number;
  hasOwner: boolean;
  ownerId: string | null;
  deputyId: string | null;
  path: string;
}

export interface WikiGraphEdge {
  a: string;
  b: string;
  relType: string;
  /** human=사람 확정(실선) · evidence=근거 공유(가는 실선) · ai-daily=AI 판정(점선) */
  source: 'human' | 'evidence' | 'ai-daily';
  confidence: number | null;
  reason: string | null;
}

export interface WikiGraph {
  nodes: WikiGraphNode[];
  edges: WikiGraphEdge[];
  /** 고른 카테고리(slug). null = 전체 */
  category?: string | null;
  /** AI 층의 계산 시각 — 전독이 실패한 날은 어제 값이 남는다. 범례에 상시 표시 */
  aiComputedAt: string | null;
}

export interface WikiOwner {
  doc_id: string;
  owner_id: string;
  deputy_id: string | null;
  effective_from: string;
  changed_by: string;
}

export interface SourceCandidate {
  id: string;
  label: string;
  sub: string;
  extra: string;
}

export interface SourceField {
  column: string;
  value: string;
}

/**
 * 문서 목록의 한 줄 (PR-3 — 출처·카테고리·정리본 요약을 함께 준다).
 *
 * ⛔ `publishedCount` 를 화면이 반드시 써야 한다. 이 숫자가 없으면 **제목만 있고 속이
 *    빈 문서가 내용 있는 문서와 똑같이 보인다** — 눌러 봐야 비었다는 것을 알게 된다.
 */
export interface WikiTagChip {
  slug: string;
  name: string;
}

/** 카테고리(시드 10종 + 미분류). adminOnly 는 관리자 전용(개발 기록) */
export interface WikiCategory {
  slug: string;
  name: string;
  description: string;
  sortOrder: number;
  paletteIndex: number | null;
  adminOnly: boolean;
  count: number;
}

/** 문서의 카테고리 표현 — status: none(미분류) | ai(AI 분류) | human(사람이 고정) */
export interface WikiDocCategory {
  slug: string;
  name: string;
  paletteIndex: number | null;
  status: 'none' | 'ai' | 'human';
}

/** 문서 출처 — connector: notion|slack|slack-daily|erp-action|local-project|legacy|human|unknown */
export interface WikiDocSource {
  connector: string;
  label: string;
  url: string | null;
}

export interface WikiDocRefinedBrief {
  status: 'pending' | 'ok' | 'failed' | 'stale';
  summary: string;
  refinedAt: string | null;
  humanEdited: boolean;
}

export interface WikiDocSummary {
  id: string;
  slug: string;
  title: string;
  summary: string;
  path: string;
  category: WikiDocCategory;
  source: WikiDocSource;
  refined: WikiDocRefinedBrief | null;
  /** AI 가 붙인 태그(W4). 문서 카드·필터에 쓴다 */
  tags: WikiTagChip[];
  imageCount: number;
  updatedAt: string;
  createdAt: string;
  /** 승인된 문단 수 — 이것이 0 이면 정본이 아직 없다 */
  publishedCount: number;
  draftCount: number;
  /** 문단이 아예 없는 문서 */
  isEmpty: boolean;
}

/** 문서 목록 조회 조건 (PR-3) */
export interface WikiDocListParams {
  page?: number;
  limit?: number;
  category?: string | null;
  source?: string | null;
  tag?: string | null;
  q?: string | null;
}

export interface WikiDocList {
  items: WikiDocSummary[];
  total: number;
  page: number;
  totalPages: number;
}

/** 정리본(REFINED) — 사람이 고칠 수 있는 층. version/inputHash 는 저장할 때 그대로 돌려보낸다(충돌 감지) */
export interface WikiDocRefined {
  refinedMd: string;
  summary: string;
  keyPoints: string[];
  status: 'pending' | 'ok' | 'failed' | 'stale';
  version: number;
  rawVersion: number;
  inputHash: string;
  model: string | null;
  humanEdited: boolean;
  editedBy: string | null;
  editedAt: string | null;
  refinedAt: string | null;
  /** 관리자에게만 내려온다 */
  error?: string | null;
  /** 편집기 JSON 정본(PR-8). 있으면 화면은 이것을 그리고 refinedMd 는 검색·AI 용 파생이다 */
  docJson?: WikiEditorDoc | null;
}

/** 편집기(TipTap) 문서 JSON — 서버는 {type:'doc'} 객체인지만 확인한다 */
export interface WikiEditorDoc { type: 'doc'; content?: any[] }

/** 블록 댓글(P2-2) */
export interface WikiComment {
  id: string;
  blockId: string;
  parentId: string | null;
  author: { id: string | null; name: string };
  body: string;
  mentions: string[];
  anchorText: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
  mine: boolean;
}
export interface WikiCommentList { items: WikiComment[]; counts: Record<string, number> }

/** «편집 중» 소프트 잠금 결과(PR-8) */
export interface WikiHandover { requesterName: string; at: string }
export type WikiEditLockResult =
  | { ok: true; runId: string; ttlSec: number; handover?: WikiHandover | null }
  | { ok: false; holder: { id: string | null; name: string; since?: string | null; until?: string | null }; ttlSec: number };
/** A안: 지금 편집 중 보유자(폴링). 내가 보유자면 requested 도 온다 */
export interface WikiEditLockState { holder: { id: string | null; name: string; since?: string | null } | null; requested?: { requesterId: string; requesterName: string } | null }

/** 문서 안 이미지의 글자·설명. sensitive/hidden 이면 비관리자에게는 텍스트가 null 이다 */
export interface WikiDocMedia {
  key: string;
  url: string;
  status: 'ok' | 'failed' | 'skipped' | 'pending';
  kind: string | null;
  sensitive: boolean | null;
  hidden: boolean | null;
  analyzedAt: string | null;
  description: string | null;
  ocrText: string | null;
}

export interface WikiRelatedDoc {
  id: string;
  slug: string;
  title: string;
  path: string;
  source: 'human' | 'evidence' | 'ai-daily';
  relType: string;
  confidence: number | null;
}

/** 문서 한 건 (PR-3) */
export interface WikiDocDetail {
  page: { id: string; slug: string; title: string; summary: string; status: string; updatedAt?: string; archiveNote?: string | null };
  blocks: WikiBlock[];
  refined: WikiDocRefined | null;
  media: WikiDocMedia[];
  source: WikiDocSource;
  category: WikiDocCategory;
  tags: WikiTagChip[];
  related: WikiRelatedDoc[];
  links: { self: string };
}

export interface WikiRefinedVersion {
  id: string;
  version: number;
  summary: string;
  source: 'human' | 'ai';
  edited_by: string | null;
  saved_at: string;
  refined_md?: string;
}

/** 태그 목록 항목(W4) — 많이 쓰인 순 */
export interface WikiTag {
  slug: string;
  name: string;
  count: number;
}

class WikiApi {
  async getEntityTypes(): Promise<WikiEntityType[]> {
    const res = await api.get('/wiki/entity-types');
    return (res as any).data || [];
  }

  async getEntityPage(type: string, id: string): Promise<WikiEntityPage> {
    const res = await api.get(`/wiki/e/${encodeURIComponent(type)}/${encodeURIComponent(id)}`);
    return (res as any).data;
  }

  /** 이력 더 보기 — `before` 커서로 이어 받는다 */
  async getTimeline(
    type: string,
    id: string,
    opts: { before?: string | null; limit?: number } = {}
  ): Promise<{ items: WikiTimelineItem[]; nextCursor: string | null }> {
    const params: Record<string, string | number> = {};
    if (opts.before) params.before = opts.before;
    if (opts.limit) params.limit = opts.limit;
    const res = await api.get(`/wiki/e/${encodeURIComponent(type)}/${encodeURIComponent(id)}/timeline`, { params });
    return { items: (res as any).data || [], nextCursor: (res as any).metadata?.nextCursor ?? null };
  }

  async search(q: string, limit = 20): Promise<WikiSearchResult> {
    const res = await api.get('/wiki/search', { params: { q, limit } });
    return (res as any).data || { blocks: [], documents: [] };
  }

  // ── 쓰기 ─────────────────────────────────────────────
  // ⛔ 작성은 항상 초안이다. 정본으로 만들려면 근거를 붙이고 승인해야 한다.

  async createBlock(
    type: string,
    id: string,
    body: { contentMd: string; blockType?: string; claimBasis?: string }
  ): Promise<{ page: { id: string }; block: WikiBlock }> {
    const res = await api.post(`/wiki/e/${encodeURIComponent(type)}/${encodeURIComponent(id)}/blocks`, body);
    return (res as any).data;
  }

  /** 문단 순서 바꾸기(W5) — id 배열 순서대로 표시 순서를 다시 매긴다(내용·승인 불변) */
  async reorderBlocks(ids: string[]): Promise<void> {
    await api.post('/wiki/blocks/reorder', { ids });
  }

  async updateBlock(blockId: string, body: { contentMd?: string; blockType?: string }): Promise<WikiBlock> {
    const res = await api.put(`/wiki/blocks/${blockId}`, body);
    return (res as any).data;
  }

  /** 근거로 쓸 레코드 찾기 — 허용 목록 안에서만 찾는다 */
  async searchSources(type: string, q: string, limit = 20): Promise<SourceCandidate[]> {
    if (q.trim().length < 2) return [];
    const res = await api.get('/wiki/sources/search', { params: { type, q, limit } });
    return (res as any).data || [];
  }

  /** 고른 레코드에서 근거로 삼을 칸 목록 (값도 함께 온다) */
  async getSourceFields(type: string, id: string): Promise<SourceField[]> {
    const res = await api.get('/wiki/sources/fields', { params: { type, id } });
    return (res as any).data || [];
  }

  async addSource(
    blockId: string,
    body: {
      sourceKind: string;
      /** 항목 종류. 이것을 보내면 서버가 표를 정한다 (표 이름을 화면이 알 필요 없다) */
      entityType?: string;
      tableName?: string;
      recordId?: string;
      columnName?: string;
      url?: string;
      note?: string;
    }
  ): Promise<WikiBlockSource> {
    const res = await api.post(`/wiki/blocks/${blockId}/sources`, body);
    return (res as any).data;
  }

  /**
   * 초안 문단을 공개한다 (PR-3 · 검토 단계 없음). 메모는 쓰는 즉시 공개되므로,
   * 이 통로는 «공개에 실패해 초안으로 남은» 문단을 다시 공개할 때만 쓴다.
   */
  async publishBlock(blockId: string, seenUpdatedAt?: string | null): Promise<WikiBlock> {
    const res = await api.post(`/wiki/blocks/${blockId}/publish`, { seenUpdatedAt: seenUpdatedAt || null });
    return (res as any).data;
  }

  async deleteBlock(blockId: string): Promise<void> {
    await api.delete(`/wiki/blocks/${blockId}`);
  }

  // ── 문서 페이지 ──────────────────────────────────────
  // ⛔ 이게 없어서 옮겨 온 지식문서 30건이 어디서도 열리지 않았다.

  /** 문서 목록 — 페이지네이션 + 필터(카테고리·출처·태그·검색어). 서버가 열람 권한을 이미 걸렀다 */
  async listDocs(params: WikiDocListParams = {}): Promise<WikiDocList> {
    const q: Record<string, unknown> = { page: params.page || 1, limit: params.limit || 50 };
    if (params.category) q.category = params.category;
    if (params.source) q.source = params.source;
    if (params.tag) q.tag = params.tag;
    if (params.q) q.q = params.q;
    const res = await api.get('/wiki/docs', { params: q });
    const r = res as any;
    return {
      items: r.data || [],
      total: r.metadata?.total ?? (r.data || []).length,
      page: r.metadata?.page ?? 1,
      totalPages: r.metadata?.totalPages ?? 1,
    };
  }

  /** 카테고리 목록(시드 + 열람 가능한 문서 수 + 미분류). 지도·목록 선택기가 쓴다 */
  async listCategories(): Promise<WikiCategory[]> {
    const res = await api.get('/wiki/categories');
    return (res as any).data || [];
  }

  /** 태그 목록(W4) — 많이 쓰인 순. 문서 목록 화면의 태그 칩 필터가 쓴다. */
  async listTags(): Promise<WikiTag[]> {
    const res = await api.get('/wiki/tags');
    return (res as any).data || [];
  }

  async getDocPage(slug: string): Promise<WikiDocDetail> {
    const res = await api.get(`/wiki/docs/${encodeURIComponent(slug)}`);
    return (res as any).data;
  }

  /** 문서에 메모 추가 — 쓰는 즉시 공개(PR-3). published:false 면 초안으로 남은 것 */
  async createDocBlock(
    slug: string,
    body: { contentMd: string; blockType?: string; claimBasis?: string }
  ): Promise<{ page: { id: string }; block: WikiBlock; published: boolean; publishError: string | null }> {
    const res = await api.post(`/wiki/docs/${encodeURIComponent(slug)}/blocks`, body);
    return (res as any).data;
  }

  /**
   * 정리본 저장 — expectedVersion/expectedInputHash 를 함께 보낸다.
   *   409 + reason 'version'     : 그 사이 다른 사람이 고쳤다 → 새로고침
   *   409 + reason 'raw_changed' : 그 사이 원문이 바뀌어 정리본이 다시 만들어졌다 → 새로고침
   */
  async updateRefined(
    slug: string,
    body: { refinedMd: string; docJson?: WikiEditorDoc | null; summary?: string; keyPoints?: string[]; expectedVersion: number; expectedInputHash?: string }
  ): Promise<{ version: number; refined_md: string; summary: string; key_points: string[]; human_edited: boolean; doc_json?: WikiEditorDoc | null; coalesced?: boolean }> {
    const res = await api.put(`/wiki/docs/${encodeURIComponent(slug)}/refined`, body);
    return (res as any).data;
  }

  // ── 노션식 인라인 편집(PR-8) ───────────────────────────
  /** 제목 인라인 편집 — 수집 재실행이 덮어쓰지 않는다 */
  async setDocTitle(slug: string, title: string): Promise<{ id: string; title: string; unchanged?: boolean }> {
    const res = await api.put(`/wiki/docs/${encodeURIComponent(slug)}/title`, { title });
    return (res as any).data;
  }

  /** 정리본이 없는 문서 — 원문을 그대로 복사해 편집 가능한 v1 을 만든다(원문은 그대로) */
  async createRefinedFromRaw(slug: string): Promise<{ version: number; status: string }> {
    const res = await api.post(`/wiki/docs/${encodeURIComponent(slug)}/refined/from-raw`);
    return (res as any).data;
  }

  /**
   * «편집 중» 잠금 — 60초 임차, 20초마다 다시 부른다(같은 runId 면 연장).
   *   다른 사람이 잡고 있으면 409 → { ok:false, holder } 로 돌려준다(던지지 않는다).
   */
  async claimEditLock(slug: string, runId: string): Promise<WikiEditLockResult> {
    try {
      const res = await api.post(`/wiki/docs/${encodeURIComponent(slug)}/edit-lock`, { runId });
      return (res as any).data;
    } catch (e: any) {
      if (e?.response?.status === 409 && e?.response?.data?.data) return e.response.data.data as WikiEditLockResult;
      throw e;
    }
  }

  async releaseEditLock(slug: string, runId: string): Promise<void> {
    await api.delete(`/wiki/docs/${encodeURIComponent(slug)}/edit-lock`, { data: { runId } });
  }
  /** A안: 지금 누가 편집 중인지 읽기(잠금 안 잡음) — 배지·폴링용 */
  async getEditLock(slug: string): Promise<WikiEditLockState> {
    const res = await api.get(`/wiki/docs/${encodeURIComponent(slug)}/edit-lock`);
    return (res as any).data;
  }
  /** A안: 편집 넘겨받기 요청 → 현재 편집자에게 알림. no_holder 면 바로 편집 가능 */
  async requestHandover(slug: string): Promise<{ ok: boolean; reason?: string; holder?: { name: string } }> {
    const res = await api.post(`/wiki/docs/${encodeURIComponent(slug)}/edit-handover`);
    return (res as any).data;
  }

  // ── 첨부 파일(P2-3) — 문서 스코프. 업로드는 허용 목록·20MB·내용 검사, 내려받기는 열람 판정 뒤 서명 주소 ──
  async uploadDocMedia(slug: string, file: File): Promise<{ url: string; key: string; ext: string; kind: 'image' | 'file'; mime: string; size: number; name: string }> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('name', file.name);
    const res = await api.post(`/wiki/docs/${encodeURIComponent(slug)}/media`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return (res as any).data;
  }
  async getDocMediaUrl(slug: string, appPath: string): Promise<{ url: string; name: string; kind: string }> {
    const file = appPath.replace(/^\/api\/wiki\/media\//, '');
    const res = await api.get(`/wiki/docs/${encodeURIComponent(slug)}/media/${encodeURIComponent(file)}`);
    return (res as any).data;
  }

  // ── 블록 댓글(P2-2) — 서버가 slug 스코프로 열람·권한을 판정한다. 기능이 꺼져 있으면 404 ──
  async listComments(slug: string): Promise<WikiCommentList> {
    const res = await api.get(`/wiki/docs/${encodeURIComponent(slug)}/comments`);
    return (res as any).data;
  }
  /** expectedVersion = 지금 보고 있는 문서 판. 409 reason: version(새로고침) · block_not_saved(저장 뒤 다시) · 400 needs_json(댓글 켜기) */
  async addComment(slug: string, body: { blockId: string; body: string; parentId?: string | null; expectedVersion: number }): Promise<WikiComment> {
    const res = await api.post(`/wiki/docs/${encodeURIComponent(slug)}/comments`, body);
    return (res as any).data;
  }
  async updateComment(slug: string, id: string, body: string): Promise<WikiComment> {
    const res = await api.put(`/wiki/docs/${encodeURIComponent(slug)}/comments/${encodeURIComponent(id)}`, { body });
    return (res as any).data;
  }
  async deleteComment(slug: string, id: string): Promise<void> {
    await api.delete(`/wiki/docs/${encodeURIComponent(slug)}/comments/${encodeURIComponent(id)}`);
  }
  async resolveComment(slug: string, id: string, resolved: boolean): Promise<WikiComment> {
    const res = await api.post(`/wiki/docs/${encodeURIComponent(slug)}/comments/${encodeURIComponent(id)}/${resolved ? 'resolve' : 'unresolve'}`);
    return (res as any).data;
  }

  /** 카테고리 지정(사람이 정하면 AI 가 덮어쓰지 않는다). null = 미분류로 되돌리기 */
  async setDocCategory(slug: string, categorySlug: string | null): Promise<{ category_slug: string | null; category_status: string }> {
    const res = await api.put(`/wiki/docs/${encodeURIComponent(slug)}/category`, { categorySlug });
    return (res as any).data;
  }

  /** «다시 정리» — AI 가 원문으로 정리본을 다시 만든다 (하루 20회) */
  async refineDoc(slug: string): Promise<{ ok?: boolean; refined?: number; failed?: number; skipped?: number }> {
    const res = await api.post(`/wiki/docs/${encodeURIComponent(slug)}/refine`);
    return (res as any).data || {};
  }

  async listRefinedVersions(slug: string): Promise<WikiRefinedVersion[]> {
    const res = await api.get(`/wiki/docs/${encodeURIComponent(slug)}/refined/versions`);
    return (res as any).data || [];
  }

  async getRefinedVersion(slug: string, versionId: string): Promise<WikiRefinedVersion> {
    const res = await api.get(`/wiki/docs/${encodeURIComponent(slug)}/refined/versions/${encodeURIComponent(versionId)}`);
    return (res as any).data;
  }

  /** 이전 판 되돌리기 — 매니저 이상 */
  async restoreRefinedVersion(slug: string, versionId: string): Promise<void> {
    await api.post(`/wiki/docs/${encodeURIComponent(slug)}/refined/restore`, { versionId });
  }

  // ── 관계 지도 (신경망 M1) ────────────────────────────
  // ⛔ 권한 필터는 서버가 건다 — 여기 오는 데이터는 이미 걸러진 것이다.

  async getGraph(category?: string | null): Promise<WikiGraph> {
    const res = await api.get('/wiki/graph', { params: category ? { category } : {} });
    return (res as any).data;
  }

  /** AI 연결을 사람이 확정 — 실선(human)으로 승격 */
  async confirmRelation(docA: string, docB: string): Promise<void> {
    await api.post('/wiki/relations/confirm', { docA, docB });
  }

  /** AI 연결에 「틀림」 표시 — 다시 생기지 않는다 */
  async suppressRelation(docA: string, docB: string): Promise<void> {
    await api.post('/wiki/relations/suppress', { docA, docB });
  }

  /** 사람 확정 연결 해제 */
  async removeRelation(docA: string, docB: string): Promise<void> {
    await api.delete('/wiki/relations', { data: { docA, docB } });
  }

  /** AI 자동 문서(wi-) 삭제 / 참조 거부(관리자). suppress=true 면 다시 안 올라온다. */
  async deleteAiDoc(slug: string, suppress: boolean): Promise<{ deleted_pages: number; mode: string }> {
    const res = await api.post('/wiki/admin/doc/delete', { slug, suppress });
    return (res as any).data?.data;
  }

  async listWikiOwners(): Promise<WikiOwner[]> {
    const res = await api.get('/wiki/owners');
    return (res as any).data || [];
  }

  /** 담당자 지정 — 관리자 전용 (서버·DB 이중 검사) */
  async setWikiOwner(docId: string, ownerId: string, deputyId?: string | null): Promise<void> {
    await api.post('/wiki/owners', { docId, ownerId, deputyId: deputyId || null });
  }

  // ── 위키 미디어 (R3) ────────────────────────────────
  //
  // 노션 이미지/파일을 우리 S3 로 재호스팅한 것을 «서명 주소» 로 받는다.
  //   화면(<img>)은 인증 헤더를 못 싣기 때문에, 여기서(인증된 apiClient 로) 서명 주소를 받아
  //   그 주소로 이미지를 로드한다. 만료(1시간)되면 화면이 다시 이 함수를 부른다.
  //   `/api/wiki/media/<40hex>.<ext>` 형태의 앱 주소를 받아 실제 S3 서명 주소를 돌려준다.
  async getMediaSignedUrl(appPath: string): Promise<string> {
    // appPath 예: /api/wiki/media/ab12….png → apiClient baseURL(/api) 기준으로 /wiki/media/… 로 호출
    const rel = appPath.replace(/^\/api/, '');
    const res = await api.get(rel);
    const url = (res as any)?.data?.url;
    if (!url) throw new Error('이미지 주소를 받지 못했습니다.');
    return String(url);
  }

  /**
   * 편집기 이미지 업로드(W5) — 붙여넣기·드래그한 이미지를 우리 S3 로. 만료 없는 앱 주소를 돌려준다.
   *   그 주소를 마크다운 `![](…)` 로 넣으면 WikiMarkdown 이 서명 주소로 렌더한다.
   */
  async uploadMedia(file: File | Blob): Promise<string> {
    const form = new FormData();
    form.append('file', file);
    // multipart 경계(boundary)는 axios 가 채운다 — 이 저장소의 다른 업로드와 같은 패턴.
    const res = await api.post('/wiki/media/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    const url = (res as any)?.data?.url;
    if (!url) throw new Error('이미지 업로드에 실패했습니다.');
    return String(url);
  }

  // ── 라이브 토큰 ──────────────────────────────────────

  /** 쓸 수 있는 토큰 목록 — 편집기에서 안내한다 */
  async getMetrics(): Promise<WikiMetric[]> {
    const res = await api.get('/wiki/metrics');
    return (res as any).data || [];
  }

  /**
   * 토큰을 실제 값으로 채운다. 못 찾은 것도 `isFound:false` 로 돌아온다.
   *
   * ⛔ `truncated` 를 버리면 안 된다. 상한을 넘어 **조회조차 하지 않은** 토큰까지
   *    「값을 찾지 못했다」로 보이면, 사람은 있지도 않은 오타를 찾게 된다.
   */
  async resolveMetrics(
    tokens: Array<{ key: string; lookup: string }>
  ): Promise<{ items: ResolvedMetric[]; truncated: boolean; limit: number }> {
    if (tokens.length === 0) return { items: [], truncated: false, limit: 0 };
    const res = await api.post('/wiki/metrics/resolve', { tokens });
    const r = res as any;
    return { items: r.data || [], truncated: !!r.metadata?.truncated, limit: r.metadata?.limit ?? 0 };
  }
}

const wikiApi = new WikiApi();
export default wikiApi;
